import { useEffect } from 'react';
import Button from '../Button.js';
import Icon from '../Icon.js';
import StepFrame from './StepFrame.js';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * DocumentStep
 *
 * The answer. Most people should end here, which is why a document is a
 * terminal in its own right and not a waypoint on the way to a support ticket.
 *
 * It is also where the plan was corrected: a document is never a dead end. Two
 * ways out sit under it. One back into the tree for "that is not my problem",
 * and one to a person for "I read it and it did not help". The second carries
 * its own reason, so an escalation from the cleaning article arrives saying the
 * cleaning article was already read and rejected, and nobody replies with a
 * link to it.
 *
 * Body content is a list of blocks rather than HTML. Support copy is written by
 * people who are not going to hand-write markup, and a string of HTML from a
 * data file is an injection waiting for someone to paste in a customer's words.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Block = ({
  block
}) => {
  const mobile = Utils.isMobile();
  if (typeof block === 'string') {
    return /*#__PURE__*/_jsx("p", {
      style: {
        ...Theme.textStyles.body({
          color: Theme.colors.text(),
          fontSize: mobile ? 15 : 16,
          lineHeight: 1.7
        }),
        marginBottom: 16
      },
      children: block
    });
  }
  if (block.type === 'steps' || block.type === 'list') {
    let ordered = block.type === 'steps';
    return /*#__PURE__*/_jsx("ol", {
      style: {
        margin: '4px 0 22px',
        padding: 0
      },
      children: block.items.map((item, index) => /*#__PURE__*/_jsxs("li", {
        style: {
          alignItems: 'flex-start',
          display: 'flex',
          gap: 12,
          marginBottom: 12
        },
        children: [ordered ? /*#__PURE__*/_jsx("span", {
          style: {
            alignItems: 'center',
            backgroundColor: Theme.tint(0.1),
            borderRadius: 999,
            color: Theme.colors.primary(),
            display: 'inline-flex',
            flexShrink: 0,
            fontFamily: Theme.fonts.body,
            fontSize: 12,
            fontWeight: 700,
            height: 24,
            justifyContent: 'center',
            marginTop: 1,
            width: 24
          },
          children: index + 1
        }) : /*#__PURE__*/_jsx(Icon, {
          color: Theme.colors.primary(),
          name: 'check-lg',
          size: 15,
          style: {
            flexShrink: 0,
            marginTop: 4
          }
        }), /*#__PURE__*/_jsx("span", {
          style: Theme.textStyles.body({
            color: Theme.colors.text(),
            fontSize: mobile ? 15 : 16,
            lineHeight: 1.65
          }),
          children: item
        })]
      }, index))
    });
  }

  /*
   * A callout. `warning` is reserved for the things that are actually about
   * safety, do not disable the sensor, do not paint over it, so that it
   * keeps meaning something. Everything else is a plain note.
   */
  if (block.type === 'note' || block.type === 'warning') {
    let warning = block.type === 'warning';
    return /*#__PURE__*/_jsxs("div", {
      style: {
        alignItems: 'center',
        backgroundColor: warning ? 'rgba(245,99,0,0.06)' : Theme.colors.offWhite,
        border: `1px solid ${warning ? 'rgba(245, 49, 0, 0.28)' : Theme.colors.border()}`,
        borderRadius: 12,
        display: 'flex',
        gap: 12,
        marginBottom: 24,
        marginTop: 4,
        padding: '16px 18px'
      },
      children: [/*#__PURE__*/_jsx(Icon, {
        color: warning ? Theme.colors.warning : Theme.colors.grey,
        name: warning ? 'house-exclamation-fill' : 'asterisk',
        size: 16,
        style: {
          flexShrink: 0,
          marginTop: 2
        }
      }), /*#__PURE__*/_jsx("span", {
        style: Theme.textStyles.body({
          color: Theme.colors.text(),
          fontSize: 14,
          lineHeight: 1.65
        }),
        children: block.text
      })]
    });
  }
  if (block.type === 'heading') {
    return /*#__PURE__*/_jsx("h2", {
      style: {
        ...Theme.textStyles.subHeading({
          fontSize: mobile ? 17 : 19
        }),
        margin: '26px 0 12px'
      },
      children: block.text
    });
  }
  return null;
};
const DocumentStep = ({
  node,
  nodeId,
  onContinue,
  onEscalate,
  onShown,
  stepKey
}) => {
  const mobile = Utils.isMobile();

  // An article that ends in a person rather than in something to try.
  const action = node.resolution === 'escalate' && Boolean(node.escalate);

  // Recorded so an escalation can report what was already read. Runs per
  // document, not per render.
  useEffect(() => {
    onShown?.(nodeId);
    // Keyed on the node, deliberately. Re-running this because a callback
    // identity changed would re-record the same document on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);
  return /*#__PURE__*/_jsxs(StepFrame, {
    eyebrow: node.eyebrow || (action ? 'What we found' : 'Here is what to do'),
    stepKey: stepKey,
    title: node.title,
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        ...Theme.styles.card(),
        boxShadow: Theme.boxShadow.card,
        padding: mobile ? '22px 20px 8px' : '30px 32px 12px'
      },
      children: (node.body || []).map((block, index) => /*#__PURE__*/_jsx(Block, {
        block: block
      }, index))
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        borderTop: `1px solid ${Theme.colors.border()}`,
        marginTop: mobile ? 28 : 36,
        paddingTop: mobile ? 22 : 26
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          ...Theme.textStyles.subHeading({
            fontSize: mobile ? 16 : 18
          }),
          display: 'block',
          marginBottom: 6
        },
        children: action ? 'What happens next' : 'Did that solve it?'
      }), /*#__PURE__*/_jsx("span", {
        style: {
          ...Theme.textStyles.body({
            fontSize: 14,
            lineHeight: 1.6
          }),
          display: 'block',
          marginBottom: 18
        },
        children: action ? node.escalate.help || 'This one is not something you can put right from here, so the next step is with us.' : 'If it did, you are all set. There is nothing else to do.'
      }), /*#__PURE__*/_jsxs("div", {
        style: {
          alignItems: mobile ? 'stretch' : 'center',
          display: 'flex',
          flexDirection: mobile ? 'column' : 'row',
          flexWrap: mobile ? 'nowrap' : 'wrap',
          gap: 12
        },
        children: [node.escalate && /*#__PURE__*/_jsx(Button, {
          icon: action ? 'arrow-right' : 'people-fill',
          label: node.escalate.label,
          onClick: onEscalate.bind(this, node.escalate.reason)
        }), node.continue && /*#__PURE__*/_jsx(Button, {
          label: node.continue.label,
          onClick: onContinue,
          type: 'outline'
        })]
      })]
    })]
  });
};
export default DocumentStep;